import { CSSProperties } from "react";

/* Fixed bottom-right dock geometry for the standalone chatbot. The dock optionally reserves a band of
   space below itself (for a coexisting element such as a TaskFlyout) and lifts above it. The dock wraps
   its content (`height: auto`) so the dock, `.AgentChatbot`, and panel all shrink together toward the
   panel's 240px min-height instead of the dock staying fixed-tall; growth is capped at the remaining
   viewport. When a corner element shares the screen the height math floors the viewport at 256px so the
   dock stops shrinking past a usable minimum on very short viewports. */
const CORNER_INSET = "1rem";
const STACK_GAP = "0.75rem";
const DOCK_WIDTH = "25rem";
const PREFERRED_HEIGHT = "32rem";
const MIN_VIEWPORT_HEIGHT = "256px";
const DOCK_Z_INDEX = 1300;

/* Normalize a caller-supplied reserved-space value to a CSS length. Numbers are treated as pixels;
   non-positive numbers (and nullish values) mean "reserve nothing". Strings pass through so callers
   can hand in a live `calc()`/`min()` expression. */
const toReservedLength = (value: number | string | null | undefined): string | null => {
   if (value === null || value === undefined) return null;
   if (typeof value === "number") return value > 0 ? `${value}px` : null;
   return value;
};

/**
 * Build the inline style for the standalone chatbot's fixed corner dock.
 *
 * @param reservedBottomSpace Height reserved below the dock for a coexisting corner element. Accepts a
 *   pixel number or any CSS length; omit or pass a non-positive number to reserve nothing.
 */
export const resolveDockStyle = (reservedBottomSpace?: number | string | null): CSSProperties => {
   const reserved = toReservedLength(reservedBottomSpace);
   const bottom = reserved ? `calc(${CORNER_INSET} + ${reserved} + ${STACK_GAP})` : CORNER_INSET;

   /* Floor the viewport the height math sees at 256px only when a corner element shares the screen, so
      the dock and its neighbor stop shrinking on very short viewports instead of collapsing past a
      usable minimum. */
   const viewportHeight = reserved ? `max(${MIN_VIEWPORT_HEIGHT}, 100dvh)` : `100dvh`;
   const availableHeight = `calc(${viewportHeight} - ${bottom} - ${CORNER_INSET})`;

   return {
      position: "fixed",
      right: CORNER_INSET,
      bottom,
      width: DOCK_WIDTH,
      /* Wrap the shell so the dock, `.AgentChatbot`, and panel shrink together toward the panel's
         min-height; the panel supplies its own default/cap via the custom properties below. */
      height: "auto",
      maxHeight: availableHeight,
      zIndex: DOCK_Z_INDEX,
      /* Let clicks fall through the empty dock area; the panel and launcher re-enable pointer events. */
      pointerEvents: "none",
      /* Publish the sizing budget to the panel stylesheet: its preferred default height and its cap. */
      ["--agent_chatbot-panel-preferred-height" as string]: PREFERRED_HEIGHT,
      ["--agent_chatbot-dock-available-height" as string]: availableHeight,
   };
};
